import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  InputAdornment,
  Alert,
  Chip,
  Divider
} from '@mui/material';
import { compareRates } from '../../utils/rateComparisonUtils';

/**
 * ServiceRateConfiguration Component
 * Allows configuration of rate type and rates for a specific service type
 * 
 * @param {Object} serviceType - The service type information
 * @param {Object} currentConfig - Current rate configuration for this provider-service combo
 * @param {Function} onChange - Callback when configuration changes
 * @param {string} defaultHourlyRate - Default hourly rate from provider (optional)
 * @param {string} defaultFlatRate - Default flat rate from provider (optional)
 * @param {string} defaultRefusalRate - Default refusal rate from provider (optional)
 */
function ServiceRateConfiguration({ serviceType, currentConfig, onChange, defaultHourlyRate, defaultFlatRate, defaultRefusalRate }) {
  // Check if this is Initial Service Call - determines if tiered rates should be shown
  const isInitialServiceCall = serviceType.name === 'Initial Service Call';
  
  // Initialize state with currentConfig if available, otherwise use defaults based on service type's rate_type
  const getInitialRateType = () => {
    if (currentConfig?.rate_type) return currentConfig.rate_type;
    return serviceType.rate_type || 'FLAT';
  };
  
  const [rateType, setRateType] = useState(getInitialRateType());
  const [flatRate, setFlatRate] = useState(currentConfig?.flat_rate || '');
  const [refusalRate, setRefusalRate] = useState(currentConfig?.refusal_rate || (defaultRefusalRate || ''));
  const [hourlyRate, setHourlyRate] = useState(currentConfig?.hourly_rate || '');
  
  // Tiered rate structure (for Initial Service Call only)
  const [perBoard, setPerBoard] = useState(currentConfig?.per_board || '');
  const [perLocation, setPerLocation] = useState(currentConfig?.per_location || '');
  const [rate1Unit, setRate1Unit] = useState(currentConfig?.rate_1_unit || '');
  const [rate2Units, setRate2Units] = useState(currentConfig?.rate_2_units || '');
  const [rate3Units, setRate3Units] = useState(currentConfig?.rate_3_units || '');
  const [rate4Units, setRate4Units] = useState(currentConfig?.rate_4_units || '');
  const [rate5Units, setRate5Units] = useState(currentConfig?.rate_5_units || '');
  const [rate6Units, setRate6Units] = useState(currentConfig?.rate_6_units || '');
  const [rate7Units, setRate7Units] = useState(currentConfig?.rate_7_units || '');
  const [rate8Units, setRate8Units] = useState(currentConfig?.rate_8_units || '');
  const [rate9Units, setRate9Units] = useState(currentConfig?.rate_9_units || '');
  const [rate10Units, setRate10Units] = useState(currentConfig?.rate_10_units || '');

  // Update local state when currentConfig changes (e.g., when loading existing provider or auto-population)
  useEffect(() => {
    if (currentConfig) {
      setRateType(currentConfig.rate_type || serviceType.rate_type || 'FLAT');
      setFlatRate(currentConfig.flat_rate || '');
      setRefusalRate(currentConfig.refusal_rate || '');
      setHourlyRate(currentConfig.hourly_rate || '');
      
      // Load tiered rates if available
      if (isInitialServiceCall) {
        setPerBoard(currentConfig.per_board || '');
        setPerLocation(currentConfig.per_location || '');
        setRate1Unit(currentConfig.rate_1_unit || '');
        setRate2Units(currentConfig.rate_2_units || '');
        setRate3Units(currentConfig.rate_3_units || '');
        setRate4Units(currentConfig.rate_4_units || '');
        setRate5Units(currentConfig.rate_5_units || '');
        setRate6Units(currentConfig.rate_6_units || '');
        setRate7Units(currentConfig.rate_7_units || '');
        setRate8Units(currentConfig.rate_8_units || '');
        setRate9Units(currentConfig.rate_9_units || '');
        setRate10Units(currentConfig.rate_10_units || '');
      }
    }
  }, [currentConfig, isInitialServiceCall, serviceType.rate_type]);

  // Notify parent component when configuration changes
  useEffect(() => {
    const config = {
      service_type_id: serviceType.id,
      service_type_name: serviceType.name,
      rate_type: rateType,
      flat_rate: rateType === 'FLAT' && !isInitialServiceCall ? parseFloat(flatRate) || null : null,
      refusal_rate: rateType === 'FLAT' ? parseFloat(refusalRate) || null : null,
      hourly_rate: rateType === 'HOURLY' ? parseFloat(hourlyRate) || null : null
    };

    // Add tiered rates for Initial Service Call when using FLAT rate
    if (isInitialServiceCall && rateType === 'FLAT') {
      config.per_board = parseFloat(perBoard) || null;
      config.per_location = parseFloat(perLocation) || null;
      config.rate_1_unit = parseFloat(rate1Unit) || null;
      config.rate_2_units = parseFloat(rate2Units) || null;
      config.rate_3_units = parseFloat(rate3Units) || null;
      config.rate_4_units = parseFloat(rate4Units) || null;
      config.rate_5_units = parseFloat(rate5Units) || null;
      config.rate_6_units = parseFloat(rate6Units) || null;
      config.rate_7_units = parseFloat(rate7Units) || null;
      config.rate_8_units = parseFloat(rate8Units) || null;
      config.rate_9_units = parseFloat(rate9Units) || null;
      config.rate_10_units = parseFloat(rate10Units) || null;
    }
    
    onChange(config);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rateType, flatRate, refusalRate, hourlyRate, serviceType.id, serviceType.name, 
      perBoard, perLocation, rate1Unit, rate2Units, rate3Units, rate4Units, rate5Units,
      rate6Units, rate7Units, rate8Units, rate9Units, rate10Units, isInitialServiceCall]);

  const handleRateTypeChange = (event) => {
    setRateType(event.target.value);
  };

  // Validation helpers
  const getFlatRateError = () => {
    if (rateType === 'FLAT' && flatRate !== '' && flatRate !== null && flatRate !== undefined) {
      const rate = parseFloat(flatRate);
      if (isNaN(rate) || rate < 0) {
        return 'Cannot be negative ($0 or greater)';
      }
      if (serviceType.flat_completion_rate && rate > serviceType.flat_completion_rate) {
        return `Cannot exceed Coca-Cola rate ($${serviceType.flat_completion_rate})`;
      }
    }
    return '';
  };

  const getRefusalRateError = () => {
    if (rateType === 'FLAT' && refusalRate !== '' && refusalRate !== null && refusalRate !== undefined) {
      const rate = parseFloat(refusalRate);
      if (isNaN(rate) || rate < 0) {
        return 'Cannot be negative ($0 or greater)';
      }
      if (serviceType.flat_refusal_rate && rate > serviceType.flat_refusal_rate) {
        return `Cannot exceed Coca-Cola rate ($${serviceType.flat_refusal_rate})`;
      }
    }
    return '';
  };

  const getHourlyRateError = () => {
    if (rateType === 'HOURLY' && hourlyRate !== '' && hourlyRate !== null && hourlyRate !== undefined) {
      const rate = parseFloat(hourlyRate);
      if (isNaN(rate) || rate < 0) {
        return 'Cannot be negative ($0 or greater)';
      }
      if (serviceType.hourly_rate && rate > serviceType.hourly_rate) {
        return `Cannot exceed Coca-Cola rate ($${serviceType.hourly_rate}/hr)`;
      }
    }
    return '';
  };

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">{serviceType.name}</Typography>
          <Chip 
            label={serviceType.code} 
            size="small" 
            color="primary" 
            variant="outlined" 
          />
        </Box>

        {serviceType.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {serviceType.description}
          </Typography>
        )}

        <Box sx={{ mb: 2, p: 1.5, bgcolor: 'background.default', borderRadius: 1 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Coca-Cola Rates (What ServiceIT receives):
          </Typography>
          <Grid container spacing={2}>
            {serviceType.flat_completion_rate && (
              <Grid item xs={4}>
                <Typography variant="body2">
                  Completion: <strong>${serviceType.flat_completion_rate}</strong>
                </Typography>
              </Grid>
            )}
            {serviceType.flat_refusal_rate && (
              <Grid item xs={4}>
                <Typography variant="body2">
                  Refusal: <strong>${serviceType.flat_refusal_rate}</strong>
                </Typography>
              </Grid>
            )}
            {serviceType.hourly_rate && (
              <Grid item xs={4}>
                <Typography variant="body2">
                  Hourly: <strong>${serviceType.hourly_rate}/hr</strong>
                </Typography>
              </Grid>
            )}
          </Grid>
        </Box>

        <Divider sx={{ my: 2 }} />

        <FormControl component="fieldset" fullWidth>
          <FormLabel component="legend" sx={{ mb: 1 }}>
            <Typography variant="subtitle1">How should this contractor be paid?</Typography>
          </FormLabel>
          <RadioGroup
            row
            value={rateType}
            onChange={handleRateTypeChange}
          >
            <FormControlLabel 
              value="FLAT" 
              control={<Radio />} 
              label="Flat Rate (per location)" 
            />
            <FormControlLabel 
              value="HOURLY" 
              control={<Radio />} 
              label="Hourly Rate" 
            />
          </RadioGroup>
        </FormControl>

        <Box sx={{ mt: 3 }}>
          {rateType === 'FLAT' ? (
            <>
              {/* Initial Service Call gets tiered rate structure */}
              {isInitialServiceCall ? (
                <>
                  <Typography variant="subtitle2" color="primary" gutterBottom sx={{ mb: 2 }}>
                    Tiered Rate Configuration (Initial Service Call)
                  </Typography>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Configure tiered rates based on your contract. You can use Per Board/Per Location OR specific unit rates (1-10 units).
                  </Alert>
                  
                  <Grid container spacing={2}>
                    {/* Per Board and Per Location */}
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Per Board Rate"
                        type="number"
                        value={perBoard}
                        onChange={(e) => setPerBoard(e.target.value)}
                        helperText="Rate per board/unit (multiplied by unit count)"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                        inputProps={{
                          min: 0,
                          step: 0.01
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Per Location Fee"
                        type="number"
                        value={perLocation}
                        onChange={(e) => setPerLocation(e.target.value)}
                        helperText="Flat fee added per location"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                        inputProps={{
                          min: 0,
                          step: 0.01
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }}>
                        <Chip label="OR Unit-Based Rates" size="small" />
                      </Divider>
                    </Grid>

                    {/* Unit-based rates */}
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        fullWidth
                        label="1-Unit Rate"
                        type="number"
                        value={rate1Unit}
                        onChange={(e) => setRate1Unit(e.target.value)}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                        inputProps={{
                          min: 0,
                          step: 0.01
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        fullWidth
                        label="2-Units Rate"
                        type="number"
                        value={rate2Units}
                        onChange={(e) => setRate2Units(e.target.value)}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                        inputProps={{
                          min: 0,
                          step: 0.01
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        fullWidth
                        label="3-Units Rate"
                        type="number"
                        value={rate3Units}
                        onChange={(e) => setRate3Units(e.target.value)}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                        inputProps={{
                          min: 0,
                          step: 0.01
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        fullWidth
                        label="4-Units Rate"
                        type="number"
                        value={rate4Units}
                        onChange={(e) => setRate4Units(e.target.value)}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                        inputProps={{
                          min: 0,
                          step: 0.01
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        fullWidth
                        label="5-Units Rate"
                        type="number"
                        value={rate5Units}
                        onChange={(e) => setRate5Units(e.target.value)}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                        inputProps={{
                          min: 0,
                          step: 0.01
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        fullWidth
                        label="6-Units Rate"
                        type="number"
                        value={rate6Units}
                        onChange={(e) => setRate6Units(e.target.value)}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                        inputProps={{
                          min: 0,
                          step: 0.01
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        fullWidth
                        label="7-Units Rate"
                        type="number"
                        value={rate7Units}
                        onChange={(e) => setRate7Units(e.target.value)}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                        inputProps={{
                          min: 0,
                          step: 0.01
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        fullWidth
                        label="8-Units Rate"
                        type="number"
                        value={rate8Units}
                        onChange={(e) => setRate8Units(e.target.value)}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                        inputProps={{
                          min: 0,
                          step: 0.01
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        fullWidth
                        label="9-Units Rate"
                        type="number"
                        value={rate9Units}
                        onChange={(e) => setRate9Units(e.target.value)}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                        inputProps={{
                          min: 0,
                          step: 0.01
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        fullWidth
                        label="10-Units Rate"
                        type="number"
                        value={rate10Units}
                        onChange={(e) => setRate10Units(e.target.value)}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                        inputProps={{
                          min: 0,
                          step: 0.01
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                    </Grid>

                    {/* Refusal Rate */}
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Contractor Refusal Rate"
                        type="number"
                        value={refusalRate}
                        onChange={(e) => setRefusalRate(e.target.value)}
                        error={!!getRefusalRateError()}
                        helperText={getRefusalRateError() || 'Amount paid per refusal (optional)'}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                        inputProps={{
                          min: 0,
                          step: 0.01
                        }}
                      />
                    </Grid>
                  </Grid>
                </>
              ) : (
                /* All other service types use simple flat rate */
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      required
                      label="Contractor Flat Rate"
                      type="number"
                      value={flatRate}
                      onChange={(e) => setFlatRate(e.target.value)}
                      error={!!getFlatRateError()}
                      helperText={getFlatRateError() || 'Amount paid per completed location'}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      }}
                      inputProps={{
                        min: 0,
                        step: 0.01
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Contractor Refusal Rate"
                      type="number"
                      value={refusalRate}
                      onChange={(e) => setRefusalRate(e.target.value)}
                      error={!!getRefusalRateError()}
                      helperText={getRefusalRateError() || 'Amount paid per refusal (optional)'}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      }}
                      inputProps={{
                        min: 0,
                        step: 0.01
                      }}
                    />
                  </Grid>
                </Grid>
              )}
            </>
          ) : (
            /* Hourly rate for all service types */
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label="Contractor Hourly Rate"
                  type="number"
                  value={hourlyRate}
                  onChange={(e) => setHourlyRate(e.target.value)}
                  error={!!getHourlyRateError()}
                  helperText={getHourlyRateError() || 'Amount paid per hour'}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    endAdornment: <InputAdornment position="end">/hr</InputAdornment>,
                  }}
                  inputProps={{
                    min: 0,
                    step: 0.01
                  }}
                />
              </Grid>
            </Grid>
          )}
        </Box>

        {/* Intelligent rate comparison warning - handles FLAT-to-FLAT, HOURLY-to-HOURLY, and cross-type comparisons */}
        {(() => {
          const contractorRate = rateType === 'FLAT' ? flatRate : hourlyRate;
          if (!contractorRate) return null;

          const comparison = compareRates({
            contractorRateType: rateType,
            contractorRate,
            serviceType
          });

          if (!comparison.willLoseMoney) return null;

          return (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                {comparison.message}
              </Typography>
              {comparison.details && (
                <Typography variant="caption" display="block" sx={{ mt: 1, opacity: 0.9 }}>
                  Profit margin: {comparison.details.profitMargin}% | 
                  Est. hours: {comparison.details.estimatedHours || 'N/A'}
                </Typography>
              )}
            </Alert>
          );
        })()}
      </CardContent>
    </Card>
  );
}

export default ServiceRateConfiguration;

